import { Alert, Button, Modal } from 'antd';
import { useState } from 'react';
import { strings } from '../../../lang';
import { useLoadingGuard } from '../../../hooks/use-loading-guard';
import { DoNotCloseTag } from '../../common/donotclose-tag';

export interface NotReachedModalProps {
  open: boolean;
  onOk: (mailbox: boolean) => Promise<void>;
  onCancel: () => void;
}

export const NotReachedModal = ({ open, onOk, onCancel }: NotReachedModalProps) => {
  const [isLoading, guard] = useLoadingGuard();
  const [pendingAction, setPendingAction] = useState<'noMailbox' | 'mailbox' | null>(null);
  const [error, setError] = useState('');

  const confirm = (mailbox: boolean, action: 'noMailbox' | 'mailbox') =>
    guard(async () => {
      setPendingAction(action);
      setError('');
      try {
        await onOk(mailbox);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : strings.adBookingPage.caseCloseError);
      } finally {
        setPendingAction(null);
      }
    });

  const cancel = () => {
    setError('');
    onCancel();
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
      <Button key="cancel" onClick={cancel} disabled={isLoading}>
        {strings.cancel}
      </Button>
      <Button
        key="mailbox"
        onClick={() => void confirm(true, 'mailbox')}
        disabled={isLoading}
        loading={pendingAction === 'mailbox'}
      >
        {strings.mailboxButton}
      </Button>
      <Button
        key="no-mailbox"
        type="primary"
        onClick={() => void confirm(false, 'noMailbox')}
        disabled={isLoading}
        loading={pendingAction === 'noMailbox'}
      >
        {strings.noMailboxButton}
      </Button>
    </div>
  );

  return (
    <Modal
      title={strings.modalTitleNotReached}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      open={open}
      onCancel={cancel}
      footer={footer}
    >
      {error && <Alert type="error" showIcon message={error} />}
      <p>{strings.modalTextNotReached}</p>
      {isLoading && <DoNotCloseTag />}
    </Modal>
  );
};
